import './scss/rivandy.scss';
import './scss/history.scss';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  resolveCharacter,
  resolveReincarnation,
  resolveBonus,
  resolveFaction,
  resolveRewardLabel,
  modesByTitle,
  rewardTypes
} from './historyData';

const formatDuration = (value) => {
  const totalSeconds = Number(value) || 0;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60)
    .toString()
    .padStart(2, '0');
  return `${minutes}m${seconds !== '00' ? ` ${seconds}s` : ''}`;
};

const formatDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('en-US', { hour12: false });
};

const buildParams = (filters, pageNumber, pageSize) => {
  const params = {
    pageNumber,
    numberPerPage: pageSize,
    sortBy: filters.sortBy,
    sortDir: filters.sortDir
  };

  if (filters.playerName.trim()) params.playerName = filters.playerName.trim();
  if (filters.gameMode) params.gameMode = filters.gameMode;
  if (filters.mapName.trim()) params.mapName = filters.mapName.trim();
  if (filters.startDate) params.fromDate = filters.startDate;
  if (filters.endDate) params.toDate = filters.endDate;
  if (filters.minWave) params.minWave = filters.minWave;
  if (filters.maxWave) params.maxWave = filters.maxWave;
  if (filters.minPlayer) params.minPlayers = filters.minPlayer;
  if (filters.maxPlayer) params.maxPlayers = filters.maxPlayer;

  return params;
};

function History() {
  const settings = useSelector((state) => state);
  const [games, setGames] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({
    playerName: '',
    gameMode: '',
    mapName: '',
    sortBy: 'date',
    sortDir: 'desc',
    startDate: '',
    endDate: '',
    minWave: '',
    maxWave: '',
    minPlayer: '',
    maxPlayer: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedGameId, setExpandedGameId] = useState(null);
  const [rewardTypeFilter, setRewardTypeFilter] = useState('all');
  const [viewMode, setViewMode] = useState('games'); // games | rewards
  const [rewardPageNumber, setRewardPageNumber] = useState(1);
  const [rewardPageSize, setRewardPageSize] = useState(30);
  const [rewardTotal, setRewardTotal] = useState(0);
  const [rewardFeed, setRewardFeed] = useState([]);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildParams(filters, pageNumber, pageSize);
      const { data } = await axios.get('https://rivandy.com:3003/game/GetHistory', { params });
      setGames(data.games || []);
      setTotal(data.total || 0);
      setError(null);
    } catch (err) {
      setGames([]);
      setTotal(0);
      setError('Unable to fetch history right now.');
    } finally {
      setLoading(false);
    }
  }, [filters, pageNumber, pageSize]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPageNumber(1);
  };

  const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize));
  const rewardTotalPages = Math.max(1, Math.ceil((rewardTotal || 0) / rewardPageSize));

  const filteredRewardFeed = useMemo(() => {
    return rewardFeed.filter((reward) => {
      if (rewardTypeFilter === 'all') return true;
      const typeA = reward.rewardType != null ? String(reward.rewardType) : '';
      const typeB = reward.rewardDefinitionType != null ? String(reward.rewardDefinitionType) : '';
      return typeA === rewardTypeFilter || typeB === rewardTypeFilter;
    });
  }, [rewardFeed, rewardTypeFilter]);

  useEffect(() => {
    const fetchRewards = async () => {
      try {
        const params = {
          pageNumber: rewardPageNumber,
          numberPerPage: rewardPageSize,
          rewardType: rewardTypeFilter !== 'all' ? rewardTypeFilter : undefined,
          playerName: filters.playerName || undefined,
          fromDate: filters.startDate || undefined,
          toDate: filters.endDate || undefined
        };
        const { data } = await axios.get('https://rivandy.com:3003/game/GetRewardHistory', { params });
        setRewardFeed(data.rewards || []);
        setRewardTotal(data.total || 0);
      } catch (e) {
        setRewardFeed([]);
        setRewardTotal(0);
      }
    };
    fetchRewards();
  }, [filters.playerName, filters.startDate, filters.endDate, rewardPageNumber, rewardPageSize, rewardTypeFilter]);

  const renderBonusTags = (bonusList) => {
    if (!bonusList || bonusList.length === 0) return <span className="tag-pill muted">-</span>;
    const items = bonusList.map((b) => {
      const bonus = resolveBonus(b.bonusId);
      const iconSrc = bonus?.iconPath || bonus?.icon || `/img/bonus/${b.bonusId}.png`;
      const label = bonus ? bonus.name : `Bonus #${b.bonusId}`;
      const count = b.count ? Number(b.count) : 1;
      return (
        <span key={`${b.bonusId}-${b.count}`} className="tag-pill bonus-pill" title={label}>
          <img src={iconSrc} alt={label} />
          <strong>x{count}</strong>
        </span>
      );
    });
    return <div className="bonus-grid">{items}</div>;
  };

  const resolveRewardVisual = (reward) => {
    const modelId = reward.rewardModelId ?? reward.modelId ?? reward.modelid ?? reward.rewardId;
    const spirit = resolveReincarnation(modelId != null ? Number(modelId) : undefined);
    return {
      label: resolveRewardLabel(reward),
      icon: spirit?.icon
    };
  };

  const renderRewardTags = (playerRewards) => {
    if (!playerRewards || playerRewards.length === 0) return <span className="tag-pill muted">-</span>;
    return playerRewards.map((r) => {
      const { label, icon } = resolveRewardVisual(r);
      return (
        <span key={`${r.id}-${r.rewardId}-${r.modeId}`} className="tag-pill success reward-pill">
          {icon ? <img src={icon} alt={label} /> : null}
          <span>{label}</span>
        </span>
      );
    });
  };

  const resolveMode = (gameMode) => {
    if (!gameMode) return null;
    return modesByTitle[(gameMode || '').toLowerCase()] || null;
  };

  const winnerLabel = (game) => {
    if (!game) return '-';
    const winner = (game.players || []).find((p) => Number(p.userId) === Number(game.winnerId));
    if (winner) return winner.username || `User #${winner.userId}`;
    if (game.winnerId != null) return `Player ${game.winnerId}`;
    return '-';
  };

  const mapNames = useMemo(() => {
    const names = new Set();
    games.forEach((g) => g.mapName && names.add(g.mapName));
    return Array.from(names).sort();
  }, [games]);

  return (
    <>
    <section className="historyPage separator">
      <header className="history-header">
        <div>
          <p className="eyebrow">Game history & rewards</p>
          <h1>Match timeline</h1>
          <p className="subhead">Browse completed matches, inspect players, bonuses, and every reward earned.</p>
        </div>
        <div className="history-stats">
          <span className="pill stat">
            <span className="pill-icon icon-games" aria-hidden="true" />
            {total} games
          </span>
          <span className="pill stat">
            <span className="pill-icon icon-rewards" aria-hidden="true" />
            {rewardTotal} rewards
          </span>
        </div>
      </header>

      <div className="history-switch">
        <button className={`switch-btn ${viewMode === 'games' ? 'is-active' : ''}`} onClick={() => setViewMode('games')}>
          Matches
        </button>
        <button className={`switch-btn ${viewMode === 'rewards' ? 'is-active' : ''}`} onClick={() => setViewMode('rewards')}>
          Rewards
        </button>
      </div>

      <section className="history-filters">
        <label className="field">
          <span>Player</span>
          <input
            type="text"
            value={filters.playerName}
            onChange={(e) => updateFilter('playerName', e.target.value)}
            placeholder="Player name contains..."
          />
        </label>
        <label className="field">
          <span>Mode</span>
          <select value={filters.gameMode} onChange={(e) => updateFilter('gameMode', e.target.value)}>
            <option value="">All</option>
            <option value="Survivor">Survivor</option>
            <option value="BattleRoyale">BattleRoyale</option>
          </select>
        </label>
        <label className="field">
          <span>Map</span>
          <input
            type="text"
            list="map-options"
            value={filters.mapName}
            onChange={(e) => updateFilter('mapName', e.target.value)}
            placeholder="Polemos..."
          />
          <datalist id="map-options">
            {mapNames.map((name) => (
              <option value={name} key={name} />
            ))}
          </datalist>
        </label>
        <label className="field">
          <span>Wave min</span>
          <input
            type="number"
            min="0"
            value={filters.minWave}
            onChange={(e) => updateFilter('minWave', e.target.value)}
            placeholder="0"
          />
        </label>
        <label className="field">
          <span>Wave max</span>
          <input
            type="number"
            min="0"
            value={filters.maxWave}
            onChange={(e) => updateFilter('maxWave', e.target.value)}
            placeholder="Any"
          />
        </label>
        <label className="field">
          <span>Players min</span>
          <input
            type="number"
            min="0"
            value={filters.minPlayer}
            onChange={(e) => updateFilter('minPlayer', e.target.value)}
            placeholder="0"
          />
        </label>
        <label className="field">
          <span>Players max</span>
          <input
            type="number"
            min="0"
            value={filters.maxPlayer}
            onChange={(e) => updateFilter('maxPlayer', e.target.value)}
            placeholder="Any"
          />
        </label>
        <label className="field">
          <span>From</span>
          <input
            type="date"
            value={filters.startDate}
            onChange={(e) => updateFilter('startDate', e.target.value)}
          />
        </label>
        <label className="field">
          <span>To</span>
          <input
            type="date"
            value={filters.endDate}
            onChange={(e) => updateFilter('endDate', e.target.value)}
          />
        </label>
        <label className="field">
          <span>Sort</span>
          <select value={filters.sortBy} onChange={(e) => updateFilter('sortBy', e.target.value)}>
            <option value="date">Date</option>
            <option value="duration">Duration</option>
            <option value="wave">Wave</option>
            <option value="players">Players</option>
            <option value="map">Map</option>
          </select>
        </label>
        <label className="field">
          <span>Direction</span>
          <select value={filters.sortDir} onChange={(e) => updateFilter('sortDir', e.target.value)}>
            <option value="desc">Newest</option>
            <option value="asc">Oldest</option>
          </select>
        </label>
        <label className="field">
          <span>Per page</span>
          <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPageNumber(1); }}>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        </label>
      </section>

      {viewMode === 'games' && (
        <section className="history-layout single">
          <section className="history-games">
            {loading && <div className="info-banner">Loading...</div>}
            {error && <div className="info-banner error">{error}</div>}
            {!loading && !error && games.length === 0 && <div className="info-banner">No game matches your filters.</div>}

            {games.map((game) => {
              const mode = resolveMode(game.gameMode);
              const faction = resolveFaction(game.factionId);
              const isBattleRoyale = (game.gameMode || '').toLowerCase() === 'battleroyale';
              const showBonuses = !isBattleRoyale && !(mode && Number(mode.id) === 3);
              const showWave = !isBattleRoyale && !(mode && Number(mode.id) === 3);
              return (
                <article key={game.gameID} className={`game-card ${expandedGameId === game.gameID ? 'is-open' : ''}`}>
                  <header className="game-card__header">
                    <div>
                      <div className="pill mode" style={{ background: mode?.color || '#0b3e2d' }}>
                        {game.gameMode || 'Mode'}
                      </div>
                      <h3>#{game.gameID} - {game.mapName || 'Unknown map'}</h3>
                      <p>
                        {formatDate(game.gameDateTime)} - {formatDuration(game.gameDuration)}
                        {showWave ? ` - Wave ${game.waveNumber ?? 0}` : ''}
                      </p>
                    </div>
                    <div className="game-card__stats">
                      <div>
                        <span>Players</span>
                        <strong>{game.playerCount}/{game.maxPlayers}</strong>
                      </div>
                      <div>
                        <span>Best score</span>
                        <strong>{game.summary?.bestScore ?? 0}</strong>
                      </div>
                      <div>
                        <span>Kills</span>
                        <strong>{game.summary?.totalKills ?? 0}</strong>
                      </div>
                    </div>
                    <button
                      className="ghost-btn"
                      onClick={() => setExpandedGameId(expandedGameId === game.gameID ? null : game.gameID)}
                    >
                      {expandedGameId === game.gameID ? 'Hide details' : 'Details'}
                    </button>
                  </header>

                  {expandedGameId === game.gameID && (
                    <div className="game-card__body">
                      <div className="game-meta">
                        <div>
                          <span className="muted">Winner</span>{' '}
                          {game.winnerId ? (
                            <Link to={`/player/${game.winnerId}`} className="link-inline">
                              {winnerLabel(game)}
                            </Link>
                          ) : (
                            winnerLabel(game)
                          )}
                        </div>
                        {faction ? <div><span className="muted">Faction</span> {faction.title}</div> : null}
                      </div>
                      <div className="table-wrap">
                        <table className="player-table" cellSpacing="0">
                          <thead>
                            <tr>
                              <th>Player</th>
                              <th>Character</th>
                              <th>Spirit</th>
                              <th className="stat-col">
                                <span className="header-icon icon-kills">Kills</span>
                              </th>
                              <th className="stat-col">
                                <span className="header-icon icon-monsters">Monsters</span>
                              </th>
                              {showBonuses ? (
                                <th>
                                  <span className="header-icon icon-bonus">Bonuses</span>
                                </th>
                              ) : null}
                              <th>
                                <span className="header-icon icon-rewards">Rewards</span>
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {(game.players || []).map((player) => {
                              const character = resolveCharacter(Number(player.classId));
                              const spiritId = player.reId ?? player.relId;
                              const spirit = resolveReincarnation(spiritId != null ? Number(spiritId) : undefined);
                              const factionPlayer = resolveFaction(player.factionId);
                              const isWinner = game.winnerId != null && Number(player.userId) === Number(game.winnerId);
                              const charTitle = character ? `${character.name}${character.characterType ? ` · ${character.characterType}` : ''}` : '-';
                              const spiritTitle = spirit ? `${spirit.name}${spirit.characterType ? ` · ${spirit.characterType}` : ''}` : '-';
                              return (
                                <tr key={`${game.gameID}-${player.userId}-${player.id}`} className={isWinner ? 'player-row winner' : 'player-row'}>
                                  <td>
                                    <Link to={`/player/${player.userId || ''}`} className="player-cell link-quiet">
                                      {player.avatarId ? <img src={player.avatarId} alt={player.username || 'avatar'} /> : <div className="avatar-fallback">?</div>}
                                      <div>
                                        <p className="player-name">{player.username || `User #${player.userId}`}</p>
                                        <p className="muted small">{factionPlayer ? factionPlayer.title : ''}</p>
                                      </div>
                                    </Link>
                                  </td>
                                  <td>
                                    <div
                                      className="role-chip"
                                      title={charTitle}
                                      aria-label={charTitle}
                                      data-title={charTitle}
                                      >
                                      {character && <img src={character.icon} alt={character.name} />}
                                      <div className="role-chip__text">
                                        <p className="label">{character ? character.name : '-'}</p>
                                        <p className="muted small">{character ? character.characterType : ''}</p>
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    <div
                                      className="role-chip"
                                      title={spiritTitle}
                                      aria-label={spiritTitle}
                                      data-title={spiritTitle}
                                      >
                                      {spirit && <img src={spirit.icon} alt={spirit.name} />}
                                      <div className="role-chip__text">
                                        <p className="label">{spirit ? spirit.name : '-'}</p>
                                        <p className="muted small">{spirit ? spirit.characterType : ''}</p>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="stat-col">{player.kills ?? 0}</td>
                                  <td className="stat-col">{player.monsterKills ?? 0}</td>
                                  {showBonuses ? <td className="tag-cell">{renderBonusTags(player.bonuses)}</td> : null}
                                  <td className="tag-cell">{renderRewardTags(player.rewards)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </article>
              );
            })}

            <div className="pager">
              <button disabled={pageNumber <= 1} onClick={() => setPageNumber((p) => Math.max(1, p - 1))}>Previous</button>
              <span>Page {pageNumber} / {totalPages}</span>
              <button disabled={pageNumber >= totalPages} onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}>Next</button>
            </div>
          </section>
        </section>
      )}

      {viewMode === 'rewards' && (
        <section className="reward-feed full">
          <header>
            <div>
              <p className="eyebrow">Rewards</p>
              <h3>Loot log</h3>
            </div>
            <select value={rewardTypeFilter} onChange={(e) => { setRewardTypeFilter(e.target.value); setRewardPageNumber(1); }}>
              <option value="all">All types</option>
              {Object.entries(rewardTypes).map(([id, label]) => (
                <option key={id} value={id}>{label}</option>
              ))}
            </select>
          </header>

          <div className="reward-list">
            {filteredRewardFeed.length === 0 && <p className="muted small">No reward found.</p>}
            {filteredRewardFeed.map((reward) => {
              const gameLabel = reward.gameId || reward.game_id || reward.gameID || '-';
              const playerLabel = reward.playerName || reward.username || `User #${reward.playerId || ''}`.trim();
              const { label, icon } = resolveRewardVisual(reward);
              return (
                <article key={`${reward.id}-${reward.rewardId}-${reward.playerId}`} className="reward-row">
                  <div className="reward-title reward-title--with-icon">
                    {icon ? <img src={icon} alt={label} /> : null}
                    <span>{label}</span>
                  </div>
                  <p className="muted small">
                    Player{' '}
                    <Link to={`/player/${reward.playerId || ''}`} className="link-inline">
                      {playerLabel}
                    </Link>{' '}
                    - Game #{gameLabel}
                  </p>
                  <p className="muted tiny">{formatDate(reward.rewardedAt)}</p>
                </article>
              );
            })}
          </div>

          <div className="pager">
            <button disabled={rewardPageNumber <= 1} onClick={() => setRewardPageNumber((p) => Math.max(1, p - 1))}>Previous</button>
            <span>Page {rewardPageNumber} / {rewardTotalPages}</span>
            <button disabled={rewardPageNumber >= rewardTotalPages} onClick={() => setRewardPageNumber((p) => Math.min(rewardTotalPages, p + 1))}>Next</button>
          </div>
        </section>
      )}
    </section>
    </>
  );
}

export default History;















